import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { dataService, Registration } from '../services/dataService';
import { ConfirmationDialog, DashboardHeader } from '../components';
import '../styles/dashboard.css';
import '../styles/dashboard-header.css';

// Grade 7 mandatory events (relay is optional)
const GRADE_7_MANDATORY_EVENTS = ['100m', '1000m', 'javelin', 'long_jump'];

// Interface for grouped student data
interface GroupedStudentEvent {
  sport: string;
  age_group: string;
  status_date?: string;
}

interface GroupedStudent extends Registration {
  events: GroupedStudentEvent[];
}

const HeadteacherDashboard: React.FC = () => {
  const { user, registrations, loadRegistrations, isLoading } = useApp();
  
  // Add animation styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(30px) scale(0.95);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  const [rejectedRegistrations, setRejectedRegistrations] = useState<GroupedStudent[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [ageGroups, setAgeGroups] = useState<any[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('all');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Mobile popup states
  const [showFiltersPopup, setShowFiltersPopup] = useState(false);
  const [showGuidelinesPopup, setShowGuidelinesPopup] = useState(false);
  
  // Student details popup states
  const [showStudentDetails, setShowStudentDetails] = useState(false);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState<any>(null);
  
  // Pagination state for approved students
  const [approvedCurrentPage, setApprovedCurrentPage] = useState(1);
  const [approvedItemsPerPage] = useState(10);
  
  const [registrationForm, setRegistrationForm] = useState({
    selectedEvents: [] as string[],
    gender: 'male' as 'male' | 'female',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    ageGroup: '' // For 13-year-old manual selection
  });
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    schools: 0
  });

  // State for unapprove functionality
  const [isUnapproving, setIsUnapproving] = useState(false);
  const [showUnapproveConfirm, setShowUnapproveConfirm] = useState(false);
  const [studentToUnapprove, setStudentToUnapprove] = useState<GroupedStudent | null>(null);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successToastMessage, setSuccessToastMessage] = useState('');

  // Helper function to group students by unique identity
  const groupStudentsByIdentity = (registrations: Registration[]): GroupedStudent[] => {
    const grouped = new Map();
    
    registrations.forEach(registration => {
      // Create unique key using first_name, last_name, date_of_birth, and gender
      const studentKey = `${registration.first_name}_${registration.last_name}_${registration.date_of_birth}_${registration.gender}`;
      
      if (grouped.has(studentKey)) {
        // Add event to existing student
        grouped.get(studentKey).events.push({
          sport: registration.sport,
          age_group: registration.age_group,
          status_date: registration.status_date
        });
      } else {
        // Create new student entry
        grouped.set(studentKey, {
          ...registration,
          events: [{
            sport: registration.sport,
            age_group: registration.age_group,
            status_date: registration.status_date
          }]
        });
      }
    });
    
    return Array.from(grouped.values());
  };

  // Age calculation function - calculates age at end of year
  const getAgeAtEndOfYear = (dateOfBirth: string): number => {
    if (!dateOfBirth) return 0;
    
    const birthDate = new Date(dateOfBirth);
    const endOfYear = new Date(new Date().getFullYear(), 11, 31); // December 31st of current year
    
    let age = endOfYear.getFullYear() - birthDate.getFullYear();
    
    // Check if birthday has passed by end of year
    const birthDateThisYear = new Date(endOfYear.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    if (endOfYear < birthDateThisYear) {
      age--;
    }
    
    return age;
  };

  // Get age group from date of birth
  const getAgeGroup = (dateOfBirth: string): string => {
    if (!dateOfBirth) return '';
    
    const age = getAgeAtEndOfYear(dateOfBirth);
    
    if (age >= 10 && age <= 12) return 'Grade 7';
    if (age >= 13 && age <= 15) return 'Under 15';
    if (age >= 16 && age <= 17) return 'Under 17';
    if (age >= 18 && age <= 20) return 'Under 20';
    
    return '';
  };

  // Check if student is Grade 7
  const isGrade7Student = (dateOfBirth: string): boolean => {
    const age = getAgeAtEndOfYear(dateOfBirth);
    return age >= 10 && age <= 12;
  };

  // Helper: Check if student is exactly 13 years old (special case)
  const is13YearsOld = (dob: string) => {
    return getAgeAtEndOfYear(dob) === 13;
  };

  // Helper: Get effective age group for student (considering manual selection for 13-year-olds)
  const getEffectiveAgeGroup = (student: any) => {
    if (is13YearsOld(student.dateOfBirth) && student.ageGroup) {
      return student.ageGroup; // Use manual selection for 13-year-olds
    }
    return getAgeGroup(student.dateOfBirth); // Use automatic calculation for others
  };

  // Get missing mandatory events for Grade 7 students
  const getMissingMandatoryEvents = (selectedEvents: string[]): string[] => {
    return GRADE_7_MANDATORY_EVENTS.filter(eventId => !selectedEvents.includes(eventId));
  };

  useEffect(() => {
    loadHeadteacherData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Filter registrations based on selected filters
    loadRegistrations(getFilters());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, selectedAgeGroup, selectedGender]);

  useEffect(() => {
    // Calculate stats when registrations change
    const filteredRegistrations = user?.role === 'headmaster' 
      ? registrations.filter(r => r.school_id === user?.schoolId)
      : registrations;
      
    const newStats = {
      total: filteredRegistrations.length,
      pending: filteredRegistrations.filter(r => r.status === 'pending' || r.status === null).length,
      approved: filteredRegistrations.filter(r => r.status === 'approved').length,
      rejected: filteredRegistrations.filter(r => r.status === 'rejected').length,
      schools: new Set(filteredRegistrations.map(r => r.schoolId)).size
    };
    setStats(newStats);
  }, [registrations, user]);

  const loadHeadteacherData = async () => {
    try {
      // Load events and age groups
      const eventsData = await dataService.getEvents();
      const ageGroupsData = await dataService.getAgeGroups();
      setEvents(eventsData);
      setAgeGroups(ageGroupsData);

      // Load all registrations and filter rejected ones
      const allRegistrations = await dataService.getRegistrations();
      const rejected = allRegistrations.filter(reg => {
        const isRejected = reg.status === 'rejected';
        const isFromUserSchool = user?.role === 'headmaster' ? reg.school_id === user?.schoolId : true;
        return isRejected && isFromUserSchool;
      });
      
      // Group rejected students by identity
      const groupedRejectedStudents = groupStudentsByIdentity(rejected);
      setRejectedRegistrations(groupedRejectedStudents);

      // Initial load of registrations will be handled by the filter useEffect
    } catch (error) {
  console.error('Failed to load headteacher data:', error);
    }
  };

  const getFilters = () => {
    const filters: any = {};
    
    if (selectedEvent !== 'all') {
      filters.eventId = selectedEvent;
    }
    
    if (selectedAgeGroup !== 'all') {
      filters.ageGroupId = selectedAgeGroup;
    }

    if (selectedGender !== 'all') {
      filters.gender = selectedGender;
    }
    
    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const getAgeGroupName = (ageGroupId: string) => {
    const ageGroupMap: { [key: string]: string } = {
      'under_12': 'Under 12',
      'under_14': 'Under 14',
      'under_16': 'Under 16',
      'under_18': 'Under 18',
      'open': 'Open',
      'Grade 7': 'Grade 7',
      'Under 15': 'Under 15',
      'Under 17': 'Under 17',
      'Under 20': 'Under 20'
    };
    return ageGroupMap[ageGroupId] || ageGroupId;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getEligibleEvents = (dateOfBirth: string, gender: string, manualAgeGroup?: string) => {
    // Use manual age group if provided (for 13-year-olds), otherwise calculate automatically
    const ageGroup = manualAgeGroup || getAgeGroup(dateOfBirth);
    const age = getAgeAtEndOfYear(dateOfBirth);
    
    // For Grade 7 students, only specific events are allowed
    if (ageGroup === 'Grade 7') {
      const grade7Events = [
        { id: '100m', name: '100m Sprint', category: 'Track' },
        { id: '1000m', name: '1000m Race', category: 'Track' },
        { id: '4x100_relay', name: '4×100m Relay', category: 'Track' },
        { id: 'long_jump', name: 'Long Jump', category: 'Field' },
        { id: 'javelin', name: 'Javelin Throw', category: 'Field' }
      ];
      return grade7Events;
    }
    
    // For Under 15 age group, use HARDCODED list - NO 1000m!
    if (ageGroup === 'Under 15') {
      const hardcodedUnder15Events = [
        { id: '100m', name: '100m Sprint', category: 'Track' },
        { id: '200m', name: '200m Sprint', category: 'Track' },
        { id: '400m', name: '400m Race', category: 'Track' },
        { id: '800m', name: '800m Race', category: 'Track' },
        { id: '1500m', name: '1500m Race', category: 'Track' },
        { id: 'high_jump', name: 'High Jump', category: 'Field' },
        { id: 'long_jump', name: 'Long Jump', category: 'Field' },
        { id: 'shot_put', name: 'Shot Put', category: 'Field' },
        { id: 'javelin', name: 'Javelin Throw', category: 'Field' },
        { id: 'discus', name: 'Discus Throw', category: 'Field' },
        { id: '4x100_relay', name: '4×100m Relay', category: 'Track' },
        { id: '4x400_relay', name: '4×400m Relay', category: 'Track' }
      ];
      return hardcodedUnder15Events;
    }
    
    // For other age groups, use existing logic
    const eventRestrictions: { [key: string]: { minAge?: number; maxAge?: number; genders?: string[] } } = {
      'javelin': { minAge: 14, genders: ['male', 'female'] },
      'shot_put': { minAge: 12, genders: ['male', 'female'] },
      'discus': { minAge: 14, genders: ['male', 'female'] },
      '1500m': { minAge: 12, genders: ['male', 'female'] },
      '800m': { minAge: 10, genders: ['male', 'female'] },
      '100m': { genders: ['male', 'female'] },
      '200m': { genders: ['male', 'female'] },
      '400m': { genders: ['male', 'female'] },
      'high_jump': { genders: ['male', 'female'] },
      'long_jump': { genders: ['male', 'female'] },
      'triple_jump': { genders: ['male', 'female'] },
      '4x100_relay': { genders: ['male', 'female'] },
      '4x400_relay': { genders: ['male', 'female'] }
    };

    return events.filter(event => {
      const restrictions = eventRestrictions[event.id];
      
      if (!restrictions) return true;
      
      if (restrictions.minAge && age < restrictions.minAge) return false;
      if (restrictions.maxAge && age > restrictions.maxAge) return false;
      if (restrictions.genders && !restrictions.genders.includes(gender)) return false;
      
      return true;
    });
  };

  // Check registration limits for a specific event, age group, and gender
  const checkRegistrationLimits = async (schoolId: string, eventId: string, gender: string, ageGroup: string) => {
    try {
      // Get existing registrations for this school, event, age group, and gender
      const existingRegistrations = registrations.filter(reg => 
        reg.school_id === schoolId && 
        reg.sport === eventId && 
        (reg.status === 'approved' || reg.status === 'pending' || reg.status === null) &&
        reg.gender === gender &&
        reg.age_group === ageGroup
      );

      // Special limit for Grade 7 - 6 students per event, others get 3 per age/gender combination
      const maxForEvent = ageGroup === 'Grade 7' ? 6 : 3;
      
      return existingRegistrations.length >= maxForEvent;
    } catch (error) {
      console.error('Error checking registration limits:', error);
      return false;
    }
  };

  const handleRegistrationFormChange = (field: string, value: string) => {
    setRegistrationForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };

      // If date of birth changes, clear event selection and age group to force re-selection based on new age
      if (field === 'dateOfBirth' && value) {
        // Clear event selection and age group to force re-selection based on new age
        newForm.selectedEvents = [];
        newForm.ageGroup = '';
      }

      // If gender changes, clear event selection
      if (field === 'gender') {
        newForm.selectedEvents = [];
      }

      return newForm;
    });
  };

  // Handle event selection with limits and relay restrictions
  const handleEventSelection = (eventId: string, isSelected: boolean) => {
    setRegistrationForm(prev => {
      const currentEvents = [...prev.selectedEvents];
      const ageGroup = getEffectiveAgeGroup(prev);
      const isGrade7 = ageGroup === 'Grade 7';
      
      if (isSelected) {
        // Check sports count based on age group
        const maxSports = isGrade7 ? 5 : 3;
        if (currentEvents.length >= maxSports) {
          if (isGrade7) {
            alert('Grade 7 students can participate in maximum 5 events (4 mandatory + relay).');
          } else {
            alert('Students can only register for maximum 3 events.');
          }
          return prev;
        }
        
        // Check relay restrictions - specific relay event IDs
        const relayEvents = ['4x100_relay', '4x400_relay'];
        const isRelay = relayEvents.includes(eventId);
        
        if (isRelay) {
          // Check if student already has ANY relay event
          const hasAnyRelay = currentEvents.some(event => relayEvents.includes(event));
          
          if (hasAnyRelay) {
            alert('Students can only register for one relay event. Please unselect the other relay first.');
            return prev;
          }
        }
        
        currentEvents.push(eventId);
        
        // For Grade 7, check mandatory events after adding
        if (isGrade7) {
          const missing = getMissingMandatoryEvents(currentEvents);
          if (currentEvents.length === maxSports && missing.length > 0) {
            const eventNames = missing.map(id => events.find(e => e.id === id)?.name || id).join(', ');
            alert(`Grade 7 students must include all mandatory events: ${eventNames}`);
          }
        }
        
      } else {
        const index = currentEvents.indexOf(eventId);
        if (index > -1) {
          currentEvents.splice(index, 1);
        }
      }
      
      return {
        ...prev,
        selectedEvents: currentEvents
      };
    });
  };

  // Check if an event should be disabled
  const isEventDisabled = (eventId: string) => {
    const currentEvents = registrationForm.selectedEvents;
    const ageGroup = getEffectiveAgeGroup(registrationForm);
    const isGrade7 = ageGroup === 'Grade 7';
    const maxSports = isGrade7 ? 5 : 3;
    
    // If already at max events and this event is not selected, disable it
    if (currentEvents.length >= maxSports && !currentEvents.includes(eventId)) {
      return true;
    }
    
    // Relay restrictions - be more strict
    const relayEvents = ['4x100_relay', '4x400_relay'];
    const isCurrentEventRelay = relayEvents.includes(eventId);
    
    if (isCurrentEventRelay) {
      // If this is a relay event, check if any OTHER relay is already selected
      const otherRelaySelected = currentEvents.some(event => 
        relayEvents.includes(event) && event !== eventId
      );
      
      if (otherRelaySelected) {
        return true;
      }
    }
    
    // Check school limit: maximum students per event per age/gender combination
    if (user?.schoolId && ageGroup && registrationForm.gender) {
      const schoolRegistrationsForEvent = registrations.filter(reg => 
        reg.sport === eventId && 
        reg.school_id === user.schoolId &&
        (reg.status === 'approved' || reg.status === 'pending' || reg.status === null) &&
        reg.gender === registrationForm.gender &&
        reg.age_group === ageGroup
      ).length;
      
      const maxForEvent = isGrade7 ? 6 : 3;
      
      if (schoolRegistrationsForEvent >= maxForEvent && !currentEvents.includes(eventId)) {
        return true;
      }
    }
    
    return false;
  };

  // Get current registration count for an event
  const getCurrentEventCount = (eventId: string) => {
    if (!user?.schoolId || !registrationForm.dateOfBirth || !registrationForm.gender) return 0;
    
    const ageGroup = getEffectiveAgeGroup(registrationForm);
    return registrations.filter(reg => 
      reg.sport === eventId && 
      reg.school_id === user.schoolId &&
      (reg.status === 'approved' || reg.status === 'pending' || reg.status === null) &&
      reg.gender === registrationForm.gender &&
      reg.age_group === ageGroup
    ).length;
  };

  // Get the reason why an event is disabled
  const getEventDisabledReason = (eventId: string) => {
    const currentEvents = registrationForm.selectedEvents;
    const ageGroup = getEffectiveAgeGroup(registrationForm);
    const isGrade7 = ageGroup === 'Grade 7';
    const maxSports = isGrade7 ? 5 : 3;
    
    if (currentEvents.length >= maxSports && !currentEvents.includes(eventId)) {
      return isGrade7 ? "Maximum 5 events per Grade 7 student" : "Maximum 3 events per student";
    }
    
    const relayEvents = ['4x100_relay', '4x400_relay'];
    const isCurrentEventRelay = relayEvents.includes(eventId);
    
    if (isCurrentEventRelay) {
      const otherRelaySelected = currentEvents.some(event => 
        relayEvents.includes(event) && event !== eventId
      );
      
      if (otherRelaySelected) {
        return "Only 1 relay event allowed";
      }
    }
    
    if (user?.schoolId && ageGroup && registrationForm.gender) {
      const schoolRegistrationsForEvent = registrations.filter(reg => 
        reg.sport === eventId && 
        reg.school_id === user.schoolId &&
        (reg.status === 'approved' || reg.status === 'pending' || reg.status === null) &&
        reg.gender === registrationForm.gender &&
        reg.age_group === ageGroup
      ).length;
      
      const maxForEvent = isGrade7 ? 6 : 3;
      
      if (schoolRegistrationsForEvent >= maxForEvent && !currentEvents.includes(eventId)) {
        const categoryText = isGrade7 
          ? `${registrationForm.gender} Grade 7` 
          : `${registrationForm.gender} ${ageGroup}`;
        return `School limit reached: ${maxForEvent} students max per ${categoryText}`;
      }
    }
    
    return "";
  };

  const handleRegistrationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !user.schoolId) {
      alert('Unable to determine school information. Please log in again.');
      return;
    }

    // Basic form validation
    if (!registrationForm.firstName.trim()) {
      alert('First name is required.');
      return;
    }
    if (!registrationForm.lastName.trim()) {
      alert('Last name is required.');
      return;
    }
    if (!registrationForm.gender) {
      alert('Gender is required.');
      return;
    }
    if (!registrationForm.dateOfBirth) {
      alert('Date of birth is required.');
      return;
    }
    if (is13YearsOld(registrationForm.dateOfBirth) && !registrationForm.ageGroup) {
      alert('Please select age group for 13-year-old student (Grade 7 or Under 15).');
      return;
    }
    if (registrationForm.selectedEvents.length === 0) {
      alert('Please select at least one event.');
      return;
    }

    try {
      // Calculate age group from date of birth
      const ageGroup = getEffectiveAgeGroup(registrationForm);
      const isGrade7 = ageGroup === 'Grade 7';
      
      // Validate Grade 7 mandatory events
      if (isGrade7) {
        const missing = getMissingMandatoryEvents(registrationForm.selectedEvents);
        if (missing.length > 0) {
          const eventNames = missing.map(id => events.find(e => e.id === id)?.name || id).join(', ');
          alert(`Grade 7 students must register for all mandatory events. Missing: ${eventNames}`);
          return;
        }
      }
      
      // Check registration limits for each selected event
      for (const eventId of registrationForm.selectedEvents) {
        const limitExceeded = await checkRegistrationLimits(user.schoolId, eventId, registrationForm.gender, ageGroup);
        if (limitExceeded) {
          const eventName = events.find(e => e.id === eventId)?.name || eventId;
          const limitText = isGrade7 ? '6' : '3';
          const categoryText = isGrade7 
            ? `${registrationForm.gender} Grade 7` 
            : `${registrationForm.gender} ${ageGroup}`;
          alert(`Registration limit exceeded for ${eventName}. Maximum ${limitText} students per school per ${categoryText} category.`);
          return;
        }
      }
      
      // Create registration for each selected event
      for (const eventId of registrationForm.selectedEvents) {
        const registrationData = {
          school_id: user.schoolId,
          first_name: registrationForm.firstName,
          last_name: registrationForm.lastName,
          date_of_birth: registrationForm.dateOfBirth,
          gender: registrationForm.gender,
          sport: eventId,
          age_group: ageGroup,
          status: 'approved' as const, // Auto-approve headmaster registrations
          created_by: user.id
        };

        // Create the registration (status_date will be set automatically)
        await dataService.createRegistration(registrationData);
      }
      
      // Reset form
      setRegistrationForm({
        selectedEvents: [],
        gender: 'male',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        ageGroup: ''
      });
      
      setShowRegistrationDialog(false);
      
      // Reload data to show new registrations
      loadHeadteacherData();
      await loadRegistrations();
      
      // Show success message
      const eventNames = registrationForm.selectedEvents.map(id => 
        events.find(e => e.id === id)?.name || id
      ).join(', ');
      setSuccessMessage(`${registrationForm.firstName} ${registrationForm.lastName} has been successfully registered and approved for ${eventNames}!`);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('Registration failed:', error);
      alert('Registration failed. Please try again.');
    }
  };

  // Handle unapproving a student (move back to pending)
  const handleUnapprove = async (student: GroupedStudent) => {
    setStudentToUnapprove(student);
    setShowUnapproveConfirm(true);
  };

  // Confirm unapprove action
  const confirmUnapprove = async () => {
    if (!studentToUnapprove) return;

    try {
      setIsUnapproving(true);
      
      // Update all registrations for this student to null status (unapproved)
      for (const event of studentToUnapprove.events) {
        // Find the registration for this specific event
        const registration = registrations.find(reg => 
          reg.first_name === studentToUnapprove.first_name &&
          reg.last_name === studentToUnapprove.last_name &&
          reg.date_of_birth === studentToUnapprove.date_of_birth &&
          reg.gender === studentToUnapprove.gender &&
          reg.sport === event.sport
        );

        if (registration) {
          await dataService.updateRegistrationStatus(registration.id, null, user?.id || '');
        }
      }
      
      // Reload data to reflect changes
      await loadRegistrations();
      loadHeadteacherData();
      
      // Show success toast
      setSuccessToastMessage(`${studentToUnapprove.first_name} ${studentToUnapprove.last_name} has been unapproved successfully!`);
      setShowSuccessToast(true);
      
      // Hide success toast after 5 seconds
      setTimeout(() => {
        setShowSuccessToast(false);
        setSuccessToastMessage('');
      }, 5000);
      
    } catch (error) {
      console.error('Unapprove failed:', error);
      alert('Failed to unapprove student. Please try again.');
    } finally {
      setIsUnapproving(false);
      setShowUnapproveConfirm(false);
      setStudentToUnapprove(null);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #1a1f2e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '1.5rem'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: '3px solid rgba(212, 175, 55, 0.2)',
          borderTopColor: '#FFD700',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{
          color: '#9CA3AF',
          fontSize: '1.1rem',
          fontFamily: "'DM Sans', sans-serif",
          margin: 0
        }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader />
      <div className="dashboard-container" style={{ 
        background: 'linear-gradient(135deg, #0A0E17 0%, #111827 50%, #1a1f2e 100%)', 
        minHeight: '100vh',
        padding: '2rem 1rem',
        paddingTop: '100px'
      }}>
      {/* Success Message */}
      {successMessage && (
        <div style={{
          position: 'fixed',
          top: '2rem',
          right: '2rem',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '12px',
          boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
          zIndex: 1000,
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out',
          border: '1px solid rgba(255, 255, 255, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '50%',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              ✓
            </div>
            <div>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Success!</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>{successMessage}</div>
            </div>
            <button
              onClick={() => setSuccessMessage(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '1.25rem',
                cursor: 'pointer',
                padding: '0.25rem',
                marginLeft: 'auto',
                opacity: 0.7,
                borderRadius: '4px'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          marginBottom: '2rem',
          padding: '2rem 2.5rem',
          background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95) 0%, rgba(17, 24, 39, 0.95) 100%)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          position: 'relative',
          overflow: 'hidden',
          animation: 'fadeInUp 0.6s ease-out'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div>
              <h1 style={{ 
                margin: 0,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                lineHeight: '1.2',
                marginBottom: '0.5rem'
              }}>
                Headteacher Dashboard
              </h1>
              <p style={{ 
                color: '#9CA3AF', 
                fontSize: '1rem', 
                margin: 0,
                lineHeight: '1.5'
              }}>
                Review and manage all athletics registrations across schools.
              </p>
            </div>
            <button
              onClick={() => setShowRegistrationDialog(true)}
              style={{
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                color: '#0A0E17',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(255, 215, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(255, 215, 0, 0.3)';
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Register Student
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="stats-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '1.5rem', 
          marginBottom: '2rem' 
        }}>
          {/* Total Registrations */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.1s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(59, 130, 246, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '700', 
                  color: '#3b82f6',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.total}
                </div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Total Registrations
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(59, 130, 246, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Pending Review */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.2s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(245, 158, 11, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '700', 
                  color: '#f59e0b',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.pending}
                </div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Pending Review
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(245, 158, 11, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Approved */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.3s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(16, 185, 129, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '700', 
                  color: '#10b981',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.approved}
                </div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Approved
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(16, 185, 129, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                  <polyline points="20,6 9,17 4,12"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Rejected */}
          <div style={{ 
            background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
            borderRadius: '16px',
            padding: '1.5rem',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeInUp 0.5s ease-out 0.4s both'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 40px rgba(239, 68, 68, 0.2)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.4)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.3)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.2)';
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ 
                  fontSize: '2.5rem', 
                  fontWeight: '700', 
                  color: '#ef4444',
                  lineHeight: '1',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px'
                }}>
                  {stats.rejected}
                </div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '600', 
                  color: '#9CA3AF',
                  textTransform: 'uppercase', 
                  letterSpacing: '0.5px',
                  marginTop: '0.5rem'
                }}>
                  Rejected
                </div>
              </div>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(239, 68, 68, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(239, 68, 68, 0.3)'
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filters Button - Only visible on mobile */}
        <button
          className="mobile-filters-btn"
          onClick={() => setShowFiltersPopup(true)}
          style={{
            display: 'none',
            width: '100%',
            padding: '1rem',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#0A0E17',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginBottom: '1.5rem'
          }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
           Filter Registrations ({[selectedEvent !== 'all', selectedAgeGroup !== 'all', selectedGender !== 'all'].filter(Boolean).length} active)
        </button>

        {/* Desktop Filters */}
        <div className="desktop-filters" style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden',
          marginBottom: '2rem',
          animation: 'fadeInUp 0.6s ease-out 0.5s both'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.15)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Filter Registrations
            </h3>
          </div>
          
          <div style={{ padding: '2rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '2rem' 
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#9CA3AF',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Filter by Event
                </label>
                <select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    backgroundColor: '#1a1f2e',
                    color: '#E5E7EB',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#FFD700';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 215, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Events</option>
                  {events.map(event => (
                    <option key={event.id} value={event.id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#9CA3AF',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Filter by Age Group
                </label>
                <select
                  value={selectedAgeGroup}
                  onChange={(e) => setSelectedAgeGroup(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    backgroundColor: '#1a1f2e',
                    color: '#E5E7EB',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#FFD700';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 215, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Age Groups</option>
                  {ageGroups.map(ageGroup => (
                    <option key={ageGroup.id} value={ageGroup.id}>
                      {ageGroup.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '0.75rem', 
                  fontWeight: '600',
                  color: '#9CA3AF',
                  fontSize: '0.9rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Filter by Gender
                </label>
                <select
                  value={selectedGender}
                  onChange={(e) => setSelectedGender(e.target.value)}
                  style={{ 
                    width: '100%',
                    padding: '0.875rem 1rem',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    borderRadius: '12px',
                    fontSize: '1rem',
                    backgroundColor: '#1a1f2e',
                    color: '#E5E7EB',
                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#FFD700';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 215, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.2)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <option value="all">All Genders</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Registrations Table */}
        <div style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden',
          marginBottom: '2rem',
          animation: 'fadeInUp 0.6s ease-out 0.6s both'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.15)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              Approved Students for Events
            </h3>
          </div>
          
          <div style={{ padding: '2rem' }}>
            {(() => {
              const approvedRegistrations = registrations.filter(r => {
                const isApproved = r.status === 'approved';
                const isFromUserSchool = user?.role === 'headmaster' ? r.school_id === user?.schoolId : true;
                return isApproved && isFromUserSchool;
              });

              // Group students by identity
              const groupedApprovedStudents = groupStudentsByIdentity(approvedRegistrations);

              if (groupedApprovedStudents.length === 0) {
                return (
                  <div style={{ 
                    textAlign: 'center',
                    padding: '4rem 2rem',
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.05) 0%, rgba(212, 175, 55, 0.02) 100%)',
                    borderRadius: '16px',
                    border: '2px dashed rgba(212, 175, 55, 0.3)'
                  }}>
                    <h3 style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: '700', 
                      color: '#FFD700', 
                      marginBottom: '1rem',
                      fontFamily: "'Bebas Neue', sans-serif",
                      letterSpacing: '1px'
                    }}>
                      No Approved Students Yet
                    </h3>
                    <p style={{ 
                      color: '#9CA3AF', 
                      fontSize: '1.1rem',
                      maxWidth: '400px',
                      margin: '0 auto',
                      lineHeight: '1.6'
                    }}>
                      Once you approve student registrations, they will appear here ready to participate in events.
                    </p>
                  </div>
                );
              }

              // Pagination logic
              const totalItems = groupedApprovedStudents.length;
              const totalPages = Math.ceil(totalItems / approvedItemsPerPage);
              const startIndex = (approvedCurrentPage - 1) * approvedItemsPerPage;
              const endIndex = startIndex + approvedItemsPerPage;
              const currentPageItems = groupedApprovedStudents.slice(startIndex, endIndex);

              return (
                <div>
                  {/* Desktop Table */}
                  <div className="dashboard-table-container desktop-table" style={{ overflowX: 'auto' }}>
                    <table className="dashboard-table" style={{ 
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.95rem'
                    }}>
                      <thead>
                        <tr style={{ 
                          background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                          borderBottom: '1px solid rgba(212, 175, 55, 0.2)'
                        }}>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Student Name</th>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Event</th>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Age Group</th>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Gender</th>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'left',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Approved Date</th>
                          <th style={{ 
                            padding: '1rem',
                            textAlign: 'center',
                            fontWeight: '600',
                            color: '#FFD700',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em'
                          }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentPageItems.map((registration, index) => (
                          <tr 
                            key={registration.id}
                            style={{ 
                              borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.05)'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <td style={{ 
                              padding: '1rem'
                            }}>
                              <div style={{ 
                                fontWeight: '600',
                                color: '#E5E7EB',
                                marginBottom: '0.25rem'
                              }}>
                                {registration.first_name} {registration.last_name}
                              </div>
                              <div style={{ 
                                fontSize: '0.8rem',
                                color: '#9CA3AF'
                              }}>
                                DOB: {formatDate(registration.date_of_birth || registration.dateOfBirth || '')}
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1rem'
                            }}>
                              <div style={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '0.25rem' 
                              }}>
                                {registration.events.map((event: any, eventIndex: number) => (
                                  <span 
                                    key={eventIndex}
                                    style={{
                                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
                                      color: '#10b981',
                                      padding: '0.2rem 0.6rem',
                                      borderRadius: '4px',
                                      fontSize: '0.75rem',
                                      fontWeight: '500',
                                      display: 'inline-block',
                                      border: '1px solid rgba(16, 185, 129, 0.3)'
                                    }}
                                  >
                                    {event.sport || 'No Event'}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1rem'
                            }}>
                              <span style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
                                color: '#3b82f6',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '6px',
                                fontSize: '0.8rem',
                                fontWeight: '500',
                                border: '1px solid rgba(59, 130, 246, 0.3)'
                              }}>
                                {getAgeGroupName(registration.events[0]?.age_group || registration.ageGroupId || '')}
                              </span>
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              color: '#9CA3AF',
                              textTransform: 'capitalize'
                            }}>
                              {registration.gender}
                            </td>
                            <td style={{ 
                              padding: '1rem'
                            }}>
                              <div style={{ 
                                color: '#10b981',
                                fontWeight: '600',
                                fontSize: '0.9rem'
                              }}>
                                {registration.events[0]?.status_date ? formatDate(registration.events[0].status_date) : 'N/A'}
                              </div>
                              <div style={{ 
                                fontSize: '0.75rem',
                                color: '#9CA3AF',
                                marginTop: '0.25rem'
                              }}>
                                Ready to compete
                              </div>
                            </td>
                            <td style={{ 
                              padding: '1rem',
                              textAlign: 'center'
                            }}>
                              <button
                                onClick={() => handleUnapprove(registration)}
                                disabled={isUnapproving}
                                style={{
                                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                  color: '#0A0E17',
                                  border: 'none',
                                  borderRadius: '6px',
                                  padding: '0.5rem 0.75rem',
                                  fontSize: '0.75rem',
                                  fontWeight: '600',
                                  cursor: isUnapproving ? 'not-allowed' : 'pointer',
                                  transition: 'all 0.2s ease',
                                  opacity: isUnapproving ? 0.6 : 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem',
                                  margin: '0 auto'
                                }}
                                onMouseOver={(e) => !isUnapproving && (e.currentTarget.style.transform = 'translateY(-1px)')}
                                onMouseOut={(e) => !isUnapproving && (e.currentTarget.style.transform = 'translateY(0)')}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m5-16h4a1 1 0 011 1v4m-7 7l1-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {isUnapproving ? 'Unapproving...' : 'Unapprove'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Table */}
                  <div className="mobile-table">
                    <div className="dashboard-table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Student Name</th>
                            <th>Event</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentPageItems.map((registration, index) => (
                            <tr key={registration.id}>
                              <td>
                                <div className="student-name">
                                  {registration.first_name} {registration.last_name}
                                </div>
                                <div className="student-subtitle">
                                  {registration.gender} • {getAgeGroupName(registration.events[0]?.age_group || registration.ageGroupId || '')}
                                </div>
                              </td>
                              <td>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  {registration.events.map((event: any, eventIndex: number) => (
                                    <span key={eventIndex} className="event-badge">
                                      {event.sport || 'No Event'}
                                    </span>
                                  ))}
                                </div>
                              </td>
                              <td>
                                <button
                                  className="details-button"
                                  onClick={() => {
                                    setSelectedStudentDetails({
                                      ...registration,
                                      type: 'approved'
                                    });
                                    setShowStudentDetails(true);
                                  }}
                                  title="View Details"
                                >
                                  <svg fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '2rem',
                      padding: '1rem 0',
                      borderTop: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '0.9rem',
                        color: '#6b7280'
                      }}>
                        Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        gap: '0.5rem',
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={() => setApprovedCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={approvedCurrentPage === 1}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: approvedCurrentPage === 1 ? '#f9fafb' : 'white',
                            color: approvedCurrentPage === 1 ? '#9ca3af' : '#374151',
                            cursor: approvedCurrentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                          }}
                        >
                          Previous
                        </button>
                        
                        {/* Page Numbers */}
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => setApprovedCurrentPage(pageNum)}
                            style={{
                              padding: '0.5rem 0.75rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              background: approvedCurrentPage === pageNum ? '#e94a1a' : 'white',
                              color: approvedCurrentPage === pageNum ? 'white' : '#374151',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              minWidth: '40px'
                            }}
                          >
                            {pageNum}
                          </button>
                        ))}
                        
                        <button
                          onClick={() => setApprovedCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={approvedCurrentPage === totalPages}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: approvedCurrentPage === totalPages ? '#f9fafb' : 'white',
                            color: approvedCurrentPage === totalPages ? '#9ca3af' : '#374151',
                            cursor: approvedCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>


        {/* Rejected Registrations Section */}
        <div style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          overflow: 'hidden',
          marginTop: '2rem',
          animation: 'fadeInUp 0.6s ease-out 0.7s both'
        }}>
          {/* Rejected Table Header */}
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(17, 24, 39, 0.95) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(239, 68, 68, 0.2)'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem'
            }}>
              <h3 style={{ 
                fontSize: '1.25rem', 
                fontWeight: '700', 
                color: '#ef4444', 
                fontFamily: "'Bebas Neue', sans-serif",
                letterSpacing: '1px',
                textTransform: 'uppercase',
                margin: 0 
              }}>
                Rejected Registrations ({rejectedRegistrations.length})
              </h3>
            </div>
            <p style={{ 
              color: '#9CA3AF', 
              fontSize: '0.95rem',
              margin: '0.5rem 0 0 0'
            }}>
              Previously rejected student registrations
            </p>
          </div>

          {/* Rejected Table */}
          {rejectedRegistrations.length === 0 ? (
            <div style={{ 
              padding: '2rem',
              textAlign: 'center',
              color: '#9CA3AF'
            }}>
              No rejected registrations found.
            </div>
          ) : (
            <>
            {/* Desktop Table */}
            <div className="dashboard-table-container desktop-table" style={{ overflowX: 'auto' }}>
              <table className="dashboard-table" style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '0.95rem'
              }}>
                <thead>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                    borderBottom: '1px solid rgba(239, 68, 68, 0.2)'
                  }}>
                    <th style={{ 
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Student Name</th>
                    <th style={{ 
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Event</th>
                    <th style={{ 
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Age Group</th>
                    <th style={{ 
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Gender</th>
                    <th style={{ 
                      padding: '1rem',
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#ef4444',
                      fontSize: '0.85rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>Rejected Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rejectedRegistrations.map((registration, index) => (
                    <tr 
                      key={registration.id}
                      style={{ 
                        borderBottom: '1px solid rgba(239, 68, 68, 0.1)',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.05)'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ 
                        padding: '1rem'
                      }}>
                        <div style={{ 
                          fontWeight: '600',
                          color: '#E5E7EB',
                          marginBottom: '0.25rem'
                        }}>
                          {registration.first_name} {registration.last_name}
                        </div>
                        <div style={{ 
                          fontSize: '0.8rem',
                          color: '#9CA3AF'
                        }}>
                          DOB: {formatDate(registration.date_of_birth || registration.dateOfBirth || '')}
                        </div>
                      </td>
                      <td style={{ 
                        padding: '1rem'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: '0.25rem' 
                        }}>
                          {registration.events.map((event: any, eventIndex: number) => (
                            <span 
                              key={eventIndex}
                              style={{
                                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(239, 68, 68, 0.1) 100%)',
                                color: '#ef4444',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                display: 'inline-block'
                              }}
                            >
                              {event.sport || 'No Event'}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ 
                        padding: '1rem'
                      }}>
                        <span style={{
                          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(59, 130, 246, 0.1) 100%)',
                          color: '#3b82f6',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '500',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}>
                          {getAgeGroupName(registration.events[0]?.age_group || registration.ageGroupId || '')}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '1rem',
                        color: '#9CA3AF',
                        textTransform: 'capitalize'
                      }}>
                        {registration.gender}
                      </td>
                      <td style={{ 
                        padding: '1rem'
                      }}>
                        <div style={{ 
                          color: '#ef4444',
                          fontWeight: '600',
                          fontSize: '0.9rem'
                        }}>
                          {registration.events[0]?.status_date ? formatDate(registration.events[0].status_date) : 'N/A'}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem',
                          color: '#9CA3AF',
                          marginTop: '0.25rem'
                        }}>
                          Registration denied
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Table */}
            <div className="mobile-table">
              <div className="dashboard-table-container">
                <table>
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Event</th>
                      <th>Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rejectedRegistrations.map((registration, index) => (
                      <tr key={registration.id}>
                        <td>
                          <div className="student-name">
                            {registration.first_name} {registration.last_name}
                          </div>
                          <div className="student-subtitle">
                            {registration.gender} • {getAgeGroupName(registration.events[0]?.age_group || registration.ageGroupId || '')}
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {registration.events.map((event: any, eventIndex: number) => (
                              <span key={eventIndex} className="event-badge">
                                {event.sport || 'No Event'}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>
                          <button
                            className="details-button"
                            onClick={() => {
                              setSelectedStudentDetails({
                                ...registration,
                                type: 'rejected'
                              });
                              setShowStudentDetails(true);
                            }}
                            title="View Details"
                          >
                            <svg fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}
        </div>
        
        {/* Mobile Guidelines Button - Only visible on mobile */}
        <button
          className="mobile-guidelines-btn"
          onClick={() => setShowGuidelinesPopup(true)}
          style={{
            display: 'none',
            width: '100%',
            padding: '1rem',
            background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
            color: '#0A0E17',
            border: 'none',
            borderRadius: '12px',
            fontSize: '1rem',
            fontWeight: '700',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            marginTop: '2rem'
          }}
        >
          <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
           Headteacher Guidelines
        </button>
        
        {/* Desktop Action Guidelines */}
        <div className="desktop-guidelines" style={{ 
          background: 'linear-gradient(135deg, #111827 0%, #1a1f2e 100%)',
          borderRadius: '20px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: '1px solid rgba(212, 175, 55, 0.15)',
          overflow: 'hidden',
          marginTop: '3rem',
          animation: 'fadeInUp 0.6s ease-out 0.8s both'
        }}>
          <div style={{ 
            background: 'linear-gradient(135deg, rgba(10, 14, 23, 0.95) 0%, rgba(17, 24, 39, 0.9) 100%)',
            padding: '1.5rem 2rem',
            borderBottom: '1px solid rgba(212, 175, 55, 0.15)'
          }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              fontFamily: "'Bebas Neue', sans-serif",
              letterSpacing: '1px',
              textTransform: 'uppercase',
              background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFD700" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
              Headteacher Guidelines
            </h3>
          </div>
          
          <div style={{ padding: '2rem' }}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '2rem' 
            }}>
              <div style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderLeft: '4px solid #3b82f6'
              }}>
                <h4 style={{ 
                  color: '#3b82f6', 
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  Review Process
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#60a5fa'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Review student details and eligibility
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Verify age group matches date of birth
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Check for duplicate registrations
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Ensure event capacity limits are respected
                  </li>
                </ul>
              </div>
              
              <div style={{ 
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
                borderRadius: '16px',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderLeft: '4px solid #ef4444'
              }}>
                <h4 style={{ 
                  color: '#ef4444', 
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  Actions Available
                </h4>
                <ul style={{ 
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#f87171'
                }}>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>•</span>
                    Approve pending registrations
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>•</span>
                    Reject invalid or duplicate submissions
                  </li>
                  <li style={{ 
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>•</span>
                    Revoke approval if issues are discovered
                  </li>
                  <li style={{ 
                    marginBottom: '0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>•</span>
                    Use filters to manage large volumes efficiently
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Dialog Modal */}
      {showRegistrationDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem',
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1f2e 0%, #0A0E17 100%)',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '650px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 25px 80px rgba(0, 0, 0, 0.6), 0 0 40px rgba(255, 215, 0, 0.1)',
            border: '1px solid rgba(255, 215, 0, 0.2)',
            animation: 'slideUp 0.4s ease-out'
          }}>
            {/* Dialog Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem',
              borderBottom: '1px solid rgba(255, 215, 0, 0.2)',
              paddingBottom: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0A0E17" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                </div>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  fontFamily: "'Bebas Neue', sans-serif",
                  letterSpacing: '1px',
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  margin: 0
                }}>
                  Register New Student
                </h2>
              </div>
              <button
                onClick={() => setShowRegistrationDialog(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  fontSize: '1.25rem',
                  cursor: 'pointer',
                  color: 'rgba(255, 255, 255, 0.6)',
                  padding: '0.5rem 0.75rem',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                  e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
                }}
              >
                ×
              </button>
            </div>

            {/* Registration Form */}
            <form onSubmit={handleRegistrationSubmit}>
              <div style={{ display: 'grid', gap: '1.5rem' }}>
                {/* Student Name */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#FFD700',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      value={registrationForm.firstName}
                      onChange={(e) => handleRegistrationFormChange('firstName', e.target.value)}
                      required
                      placeholder="Enter first name"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#FFD700';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 0, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#FFD700',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={registrationForm.lastName}
                      onChange={(e) => handleRegistrationFormChange('lastName', e.target.value)}
                      required
                      placeholder="Enter last name"
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#FFD700';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 0, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>

                {/* Date of Birth and Gender */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#FFD700',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={registrationForm.dateOfBirth}
                      onChange={(e) => handleRegistrationFormChange('dateOfBirth', e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        colorScheme: 'dark'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#FFD700';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 0, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontWeight: '600',
                      color: '#FFD700',
                      fontSize: '0.9rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Gender
                    </label>
                    <select
                      value={registrationForm.gender}
                      onChange={(e) => handleRegistrationFormChange('gender', e.target.value)}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid rgba(255, 215, 0, 0.3)',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#1a1f2e',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#FFD700';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 0, 0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>
                </div>

                {/* Age Group Selection for 13-year-olds */}
                {registrationForm.dateOfBirth && is13YearsOld(registrationForm.dateOfBirth) && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)',
                    borderRadius: '12px',
                    border: '1px solid rgba(245, 158, 11, 0.3)'
                  }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.75rem',
                      fontWeight: '600',
                      color: '#f59e0b',
                      fontSize: '0.9rem'
                    }}>
                      ⚠️ This student is 13 years old - Please select age group: *
                    </label>
                    <select
                      value={registrationForm.ageGroup || ''}
                      onChange={(e) => {
                        handleRegistrationFormChange('ageGroup', e.target.value);
                        // Clear event selection when age group changes
                        setRegistrationForm(prev => ({
                          ...prev,
                          ageGroup: e.target.value,
                          selectedEvents: []
                        }));
                      }}
                      required
                      style={{
                        width: '100%',
                        padding: '0.875rem',
                        border: '2px solid rgba(245, 158, 11, 0.5)',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease',
                        backgroundColor: '#1a1f2e',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#f59e0b';
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(245, 158, 11, 0.3)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.5)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="">Select Age Group...</option>
                      <option value="Grade 7">Grade 7</option>
                      <option value="Under 15">Under 15</option>
                    </select>
                    {registrationForm.ageGroup && (
                      <div style={{ 
                        marginTop: '0.75rem', 
                        fontSize: '0.875rem', 
                        color: '#10b981',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{
                          width: '20px',
                          height: '20px',
                          backgroundColor: '#10b981',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem'
                        }}>✓</span>
                        Selected: {registrationForm.ageGroup}
                      </div>
                    )}
                  </div>
                )}

                {/* Show message when date of birth or gender is missing */}
                {(!registrationForm.dateOfBirth || !registrationForm.gender) && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(99, 102, 241, 0.1) 100%)',
                    border: '2px dashed rgba(59, 130, 246, 0.4)',
                    borderRadius: '16px',
                    padding: '2.5rem',
                    textAlign: 'center',
                    marginBottom: '1rem'
                  }}>
                    <div style={{ 
                      width: '64px',
                      height: '64px',
                      margin: '0 auto 1rem auto',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 25px rgba(59, 130, 246, 0.4)',
                      animation: 'pulse 2s infinite'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </div>
                    <h3 style={{ 
                      margin: '0 0 0.75rem 0', 
                      background: 'linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      fontSize: '1.25rem',
                      fontWeight: '700',
                      fontFamily: "'Bebas Neue', sans-serif",
                      letterSpacing: '1px'
                    }}>
                      Enter Student Details First
                    </h3>
                    <p style={{ 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      margin: 0,
                      fontSize: '0.95rem',
                      lineHeight: '1.5'
                    }}>
                      Please fill in the date of birth and gender above to see available events and registration counts.
                    </p>
                  </div>
                )}

                {/* Events - Only show when date of birth and gender are provided */}
                {registrationForm.dateOfBirth && registrationForm.gender && (
                  <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '0.75rem',
                        fontWeight: '600',
                        color: '#FFD700',
                        fontSize: '0.9rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Events ({isGrade7Student(registrationForm.dateOfBirth) 
                          ? 'Select up to 5 (4 mandatory + relay)' 
                          : 'Select up to 3 - Only 1 relay allowed'})
                      </label>
                      
                      {/* Age group display */}
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        borderRadius: '10px',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.75rem',
                        fontSize: '0.9rem',
                        color: '#10b981',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="8.5" cy="7" r="4"/>
                        </svg>
                        <strong>Age Group:</strong> {getEffectiveAgeGroup(registrationForm)} ({registrationForm.gender})
                      </div>

                      {getEffectiveAgeGroup(registrationForm) === 'Grade 7' && (
                        <div style={{
                          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.1) 100%)',
                          border: '1px solid rgba(245, 158, 11, 0.4)',
                          borderRadius: '10px',
                          padding: '0.75rem 1rem',
                          marginBottom: '0.75rem',
                          fontSize: '0.85rem',
                          color: '#f59e0b',
                          fontWeight: '500'
                        }}>
                          <strong>⚠️ Grade 7 Requirements:</strong> Only these 5 events are available: 100m Sprint, 1000m Race, 4×100m Relay, Long Jump, and Javelin Throw.
                        </div>
                      )}
                    <div style={{
                      border: '2px solid rgba(255, 215, 0, 0.2)',
                      borderRadius: '12px',
                      backgroundColor: 'rgba(255, 255, 255, 0.03)',
                      maxHeight: '280px',
                      overflowY: 'auto'
                    }}>
                      {(() => {
                        const effectiveAgeGroup = getEffectiveAgeGroup(registrationForm);
                        const eligibleEvents = getEligibleEvents(registrationForm.dateOfBirth, registrationForm.gender, effectiveAgeGroup);
                        
                        // Categorize events
                        const trackEvents = eligibleEvents.filter(event => 
                          ['100m', '200m', '400m', '800m', '1000m', '1500m', '3000m', '4x100_relay', '4x400_relay'].includes(event.id)
                        );
                        const fieldEvents = eligibleEvents.filter(event => 
                          ['high_jump', 'long_jump', 'triple_jump', 'shot_put', 'javelin', 'discus'].includes(event.id)
                        );
                        
                        return (
                          <>
                            {/* Track Events Section */}
                            {trackEvents.length > 0 && (
                              <div style={{ padding: '1rem', borderBottom: fieldEvents.length > 0 ? '1px solid rgba(255, 215, 0, 0.1)' : 'none' }}>
                                <h4 style={{
                                  margin: '0 0 0.75rem 0',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  color: '#10b981',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <div style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                                  }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <circle cx="12" cy="12" r="10"/>
                                    </svg>
                                  </div>
                                  Track Events
                                </h4>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: '0.5rem'
                                }}>
                                  {trackEvents.map(event => {
                                    const isSelected = registrationForm.selectedEvents.includes(event.id);
                                    const isDisabled = isEventDisabled(event.id);
                                    const disabledReason = getEventDisabledReason(event.id);
                                    const isRelay = ['4x100_relay', '4x400_relay'].includes(event.id);
                                    const isMandatory = getEffectiveAgeGroup(registrationForm) === 'Grade 7' && 
                                      GRADE_7_MANDATORY_EVENTS.includes(event.id);
                                    
                                    return (
                                      <div 
                                        key={event.id} 
                                        style={{
                                          border: `2px solid ${isSelected ? '#10b981' : 'rgba(255, 255, 255, 0.1)'}`,
                                          borderRadius: '10px',
                                          padding: '0.75rem',
                                          background: isSelected 
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)' 
                                            : 'rgba(255, 255, 255, 0.03)',
                                          opacity: isDisabled ? 0.5 : 1,
                                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                                          transition: 'all 0.3s ease',
                                          position: 'relative'
                                        }}
                                        onClick={() => !isDisabled && handleEventSelection(event.id, !isSelected)}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => {}} // Handled by div click
                                            style={{ 
                                              marginTop: '0.1rem',
                                              accentColor: '#10b981'
                                            }}
                                          />
                                          <div style={{ flex: 1 }}>
                                            <div style={{
                                              fontWeight: '600',
                                              fontSize: '0.9rem',
                                              color: isDisabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                                              marginBottom: '0.25rem'
                                            }}>
                                              {event.name}
                                              {isMandatory && (
                                                <span style={{
                                                  fontSize: '0.65rem',
                                                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                  color: 'white',
                                                  padding: '0.15rem 0.4rem',
                                                  borderRadius: '4px',
                                                  marginLeft: '0.5rem',
                                                  fontWeight: '700',
                                                  textTransform: 'uppercase'
                                                }}>
                                                  Required
                                                </span>
                                              )}
                                              {isRelay && (
                                                <span style={{
                                                  fontSize: '0.65rem',
                                                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                                                  color: 'white',
                                                  padding: '0.15rem 0.4rem',
                                                  borderRadius: '4px',
                                                  marginLeft: '0.5rem',
                                                  fontWeight: '700',
                                                  textTransform: 'uppercase'
                                                }}>
                                                  Relay
                                                </span>
                                              )}
                                            </div>
                                            
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              {registrationForm.dateOfBirth && registrationForm.gender && (
                                                <span style={{
                                                  fontSize: '0.75rem',
                                                  color: 'rgba(255, 255, 255, 0.5)',
                                                  fontWeight: '500'
                                                }}>
                                                  Registered: {getCurrentEventCount(event.id)}/{isGrade7Student(registrationForm.dateOfBirth) ? '6' : '3'}
                                                </span>
                                              )}
                                            </div>
                                            
                                            {disabledReason && (
                                              <div style={{
                                                fontSize: '0.7rem',
                                                color: '#ef4444',
                                                marginTop: '0.25rem',
                                                fontStyle: 'italic'
                                              }}>
                                                {disabledReason}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Field Events Section */}
                            {fieldEvents.length > 0 && (
                              <div style={{ padding: '1rem' }}>
                                <h4 style={{
                                  margin: '0 0 0.75rem 0',
                                  fontSize: '0.85rem',
                                  fontWeight: '600',
                                  color: '#f59e0b',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  <div style={{
                                    width: '20px',
                                    height: '20px',
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
                                  }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                                    </svg>
                                  </div>
                                  Field Events
                                </h4>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                  gap: '0.5rem'
                                }}>
                                  {fieldEvents.map(event => {
                                    const isSelected = registrationForm.selectedEvents.includes(event.id);
                                    const isDisabled = isEventDisabled(event.id);
                                    const disabledReason = getEventDisabledReason(event.id);
                                    const isMandatory = getEffectiveAgeGroup(registrationForm) === 'Grade 7' && 
                                      GRADE_7_MANDATORY_EVENTS.includes(event.id);
                                    
                                    return (
                                      <div 
                                        key={event.id} 
                                        style={{
                                          border: `2px solid ${isSelected ? '#f59e0b' : 'rgba(255, 255, 255, 0.1)'}`,
                                          borderRadius: '10px',
                                          padding: '0.75rem',
                                          background: isSelected 
                                            ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(217, 119, 6, 0.1) 100%)' 
                                            : 'rgba(255, 255, 255, 0.03)',
                                          opacity: isDisabled ? 0.5 : 1,
                                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                                          transition: 'all 0.3s ease'
                                        }}
                                        onClick={() => !isDisabled && handleEventSelection(event.id, !isSelected)}
                                      >
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            disabled={isDisabled}
                                            onChange={() => {}} // Handled by div click
                                            style={{ 
                                              marginTop: '0.1rem',
                                              accentColor: '#f59e0b'
                                            }}
                                          />
                                          <div style={{ flex: 1 }}>
                                            <div style={{
                                              fontWeight: '600',
                                              fontSize: '0.9rem',
                                              color: isDisabled ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.9)',
                                              marginBottom: '0.25rem'
                                            }}>
                                              {event.name}
                                              {isMandatory && (
                                                <span style={{
                                                  fontSize: '0.65rem',
                                                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                                  color: 'white',
                                                  padding: '0.15rem 0.4rem',
                                                  borderRadius: '4px',
                                                  marginLeft: '0.5rem',
                                                  fontWeight: '700',
                                                  textTransform: 'uppercase'
                                                }}>
                                                  Required
                                                </span>
                                              )}
                                            </div>
                                            
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                              {registrationForm.dateOfBirth && registrationForm.gender && (
                                                <span style={{
                                                  fontSize: '0.75rem',
                                                  color: 'rgba(255, 255, 255, 0.5)',
                                                  fontWeight: '500'
                                                }}>
                                                  Registered: {getCurrentEventCount(event.id)}/{isGrade7Student(registrationForm.dateOfBirth) ? '6' : '3'}
                                                </span>
                                              )}
                                            </div>
                                            
                                            {disabledReason && (
                                              <div style={{
                                                fontSize: '0.7rem',
                                                color: '#ef4444',
                                                marginTop: '0.25rem',
                                                fontStyle: 'italic'
                                              }}>
                                                {disabledReason}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {getEligibleEvents(registrationForm.dateOfBirth, registrationForm.gender, getEffectiveAgeGroup(registrationForm)).length === 0 && (
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
                          No events available for this age and gender combination.
                        </p>
                      )}
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '0.75rem',
                      padding: '0.875rem 1rem',
                      background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(255, 215, 0, 0.2)'
                    }}>
                      <div>
                        <div style={{
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          color: '#FFD700'
                        }}>
                          Selected: {registrationForm.selectedEvents.length}/{isGrade7Student(registrationForm.dateOfBirth) ? '5' : '3'} events
                        </div>
                        {isGrade7Student(registrationForm.dateOfBirth) && (
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#f59e0b',
                            marginTop: '0.25rem'
                          }}>
                            {getMissingMandatoryEvents(registrationForm.selectedEvents).length > 0 
                              ? `Missing mandatory: ${getMissingMandatoryEvents(registrationForm.selectedEvents).length} event(s)`
                              : (
                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#10b981' }}>
                                  ✓ All mandatory events selected
                                  <div style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#10b981',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    <div style={{
                                      width: '0',
                                      height: '0',
                                      borderLeft: '2px solid transparent',
                                      borderRight: '2px solid transparent',
                                      borderBottom: '3px solid white',
                                      transform: 'rotate(-45deg) translateY(-0.5px)'
                                    }}></div>
                                  </div>
                                </span>
                              )
                            }
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.5)',
                        textAlign: 'right'
                      }}>
                        {getEligibleEvents(registrationForm.dateOfBirth, registrationForm.gender).length} events available
                        <br />
                        for {getEffectiveAgeGroup(registrationForm)} {registrationForm.gender}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Form Actions */}
              <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'flex-end',
                  marginTop: '2rem',
                  borderTop: '1px solid rgba(255, 215, 0, 0.2)',
                  paddingTop: '1.5rem'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowRegistrationDialog(false)}
                    style={{
                      padding: '0.875rem 1.75rem',
                      border: '2px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '10px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '0.875rem 1.75rem',
                      border: 'none',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                      color: '#0A0E17',
                      fontSize: '1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.3)';
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="8.5" cy="7" r="4"/>
                      <line x1="20" y1="8" x2="20" y2="14"/>
                      <line x1="23" y1="11" x2="17" y2="11"/>
                    </svg>
                    Register Student
                  </button>
                </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Filters Popup */}
      {showFiltersPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowFiltersPopup(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f8fafc',
              borderRadius: '16px 16px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#e94d1a'
              }}>
                 Filter Registrations
              </h3>
              <button
                onClick={() => setShowFiltersPopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#f3f4f6';
                  (e.target as HTMLButtonElement).style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <div style={{
                display: 'grid',
                gap: '1.5rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Filter by Event
                  </label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="all">All Events</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Filter by Age Group
                  </label>
                  <select
                    value={selectedAgeGroup}
                    onChange={(e) => setSelectedAgeGroup(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="all">All Age Groups</option>
                    {ageGroups.map(ageGroup => (
                      <option key={ageGroup.id} value={ageGroup.id}>
                        {ageGroup.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontWeight: '600',
                    color: '#374151',
                    fontSize: '0.9rem'
                  }}>
                    Filter by Gender
                  </label>
                  <select
                    value={selectedGender}
                    onChange={(e) => setSelectedGender(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '2px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="all">All Genders</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '2rem'
              }}>
                <button
                  onClick={() => {
                    setSelectedEvent('all');
                    setSelectedAgeGroup('all');
                    setSelectedGender('all');
                  }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    transition: 'all 0.2s ease',
                    fontSize: '0.9rem'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4b5563')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#6b7280')}
                >
                  Reset Filters
                </button>
                
                <button
                  onClick={() => setShowFiltersPopup(false)}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#e94d1a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    fontSize: '0.9rem'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#d13f0f')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#e94d1a')}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Guidelines Popup */}
      {showGuidelinesPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowGuidelinesPopup(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f8fafc',
              borderRadius: '16px 16px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#3b82f6'
              }}>
                 Headteacher Guidelines
              </h3>
              <button
                onClick={() => setShowGuidelinesPopup(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#f3f4f6';
                  (e.target as HTMLButtonElement).style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem' }}>
              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, white 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderLeft: '4px solid #3b82f6',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  color: '#1e40af',
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Review Process:
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#1e3a8a'
                }}>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Review student details and eligibility
                  </li>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Verify age group matches date of birth
                  </li>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Check for duplicate registrations
                  </li>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Ensure event capacity limits are respected
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>•</span>
                    Approve qualified students promptly
                  </li>
                </ul>
              </div>

              <div style={{
                padding: '1.5rem',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.03) 0%, white 100%)',
                borderRadius: '12px',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                borderLeft: '4px solid #22c55e',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  color: '#15803d',
                  marginBottom: '1rem',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  Best Practices:
                </h4>
                <ul style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  color: '#166534'
                }}>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>•</span>
                    Process registrations within 2-3 business days
                  </li>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>•</span>
                    Contact coach if student details need clarification
                  </li>
                  <li style={{
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>•</span>
                    Maintain fair selection criteria across all schools
                  </li>
                  <li style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    lineHeight: '1.5'
                  }}>
                    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>•</span>
                    Keep accurate records for audit purposes
                  </li>
                </ul>
              </div>

              <div style={{
                textAlign: 'center',
                marginTop: '1.5rem'
              }}>
                <button
                  onClick={() => setShowGuidelinesPopup(false)}
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    fontSize: '1rem'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#1e40af')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#3b82f6')}
                >
                  Got it!
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student Details Popup */}
      {showStudentDetails && selectedStudentDetails && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowStudentDetails(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: selectedStudentDetails.type === 'approved' ? '#f0f9f5' : '#fef2f2',
              borderRadius: '16px 16px 0 0'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: selectedStudentDetails.type === 'approved' ? '#059669' : '#dc2626'
              }}>
                {selectedStudentDetails.type === 'approved' ? 'Approved' : 'Rejected'} Student Details
              </h3>
              <button
                onClick={() => setShowStudentDetails(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = '#f3f4f6';
                  (e.target as HTMLButtonElement).style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  (e.target as HTMLButtonElement).style.backgroundColor = 'transparent';
                  (e.target as HTMLButtonElement).style.color = '#6b7280';
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '2rem',
                marginBottom: '2rem'
              }}>
                <div>
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                     Personal Information
                  </h4>
                  <div style={{
                    background: '#f8fafc',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Name</label>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#1f2937'
                      }}>
                        {selectedStudentDetails.firstName} {selectedStudentDetails.lastName}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Date of Birth</label>
                      <div style={{
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {formatDate(selectedStudentDetails.date_of_birth || selectedStudentDetails.dateOfBirth || '')}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Gender</label>
                      <div style={{
                        fontSize: '1rem',
                        color: '#374151',
                        textTransform: 'capitalize'
                      }}>
                        {selectedStudentDetails.gender}
                      </div>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>School</label>
                      <div style={{
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {selectedStudentDetails.schoolName || 'Unknown School'}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#374151'
                  }}>
                     Event Information
                  </h4>
                  <div style={{
                    background: '#f8fafc',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Event</label>
                      <div style={{
                        display: 'inline-block',
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
                        color: '#047857',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}>
                        {selectedStudentDetails.sport || 'No Event'}
                      </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Age Group</label>
                      <div style={{
                        display: 'inline-block',
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(59, 130, 246, 0.05) 100%)',
                        color: '#1e40af',
                        padding: '0.5rem 1rem',
                        borderRadius: '8px',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                      }}>
                        {getAgeGroupName(selectedStudentDetails.age_group || selectedStudentDetails.ageGroupId || '')}
                      </div>
                    </div>

                    <div>
                      <label style={{
                        display: 'block',
                        fontSize: '0.8rem',
                        fontWeight: '600',
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '0.25rem'
                      }}>Registration Date</label>
                      <div style={{
                        fontSize: '1rem',
                        color: '#374151'
                      }}>
                        {formatDate(selectedStudentDetails.created_at || selectedStudentDetails.registrationDate || '')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{
                background: selectedStudentDetails.type === 'approved' 
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.03) 0%, white 100%)'
                  : 'linear-gradient(135deg, rgba(239, 68, 68, 0.03) 0%, white 100%)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: selectedStudentDetails.type === 'approved' 
                  ? '1px solid rgba(16, 185, 129, 0.2)'
                  : '1px solid rgba(239, 68, 68, 0.2)',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{
                  margin: '0 0 1rem 0',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: selectedStudentDetails.type === 'approved' ? '#059669' : '#dc2626'
                }}>
                   Status Information
                </h4>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  marginBottom: '1rem'
                }}>
                  <div style={{
                    fontSize: '2rem'
                  }}>
                    {selectedStudentDetails.type === 'approved' ? '' : ''}
                  </div>
                  <div>
                    <div style={{
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      color: selectedStudentDetails.type === 'approved' ? '#059669' : '#dc2626'
                    }}>
                      {selectedStudentDetails.type === 'approved' ? 'Registration Approved' : 'Registration Rejected'}
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#6b7280'
                    }}>
                      {selectedStudentDetails.type === 'approved' 
                        ? 'Student is eligible to participate in the event'
                        : 'Registration was denied for this student'
                      }
                    </div>
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '0.25rem'
                  }}>
                    {selectedStudentDetails.type === 'approved' ? 'Approval Date' : 'Rejection Date'}
                  </label>
                  <div style={{
                    fontSize: '1rem',
                    color: selectedStudentDetails.type === 'approved' ? '#059669' : '#dc2626',
                    fontWeight: '600'
                  }}>
                    {selectedStudentDetails.approvalDate ? formatDate(selectedStudentDetails.approvalDate) : 'N/A'}
                  </div>
                </div>
              </div>

              <div style={{
                textAlign: 'center'
              }}>
                <button
                  onClick={() => setShowStudentDetails(false)}
                  style={{
                    padding: '0.75rem 2rem',
                    backgroundColor: '#6b7280',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    transition: 'all 0.2s ease',
                    fontSize: '1rem'
                  }}
                  onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#4b5563')}
                  onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#6b7280')}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unapprove Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showUnapproveConfirm}
        title="Unapprove Student"
        message={`Are you sure you want to unapprove ${studentToUnapprove?.first_name} ${studentToUnapprove?.last_name}?`}
        details={[
          "This will reset their approval status",
          "The student will return to pending registrations",
          "They can be approved again later"
        ]}
        confirmText="Yes, Unapprove"
        cancelText="Cancel"
        onConfirm={confirmUnapprove}
        onCancel={() => {
          setShowUnapproveConfirm(false);
          setStudentToUnapprove(null);
        }}
        type="warning"
      />

      {/* Success Toast */}
      {showSuccessToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#10b981',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          minWidth: '300px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>
            <div style={{ fontWeight: '600', fontSize: '0.9rem' }}>Success!</div>
            <div style={{ fontSize: '0.85rem', opacity: 0.9, marginTop: '0.25rem' }}>
              {successToastMessage}
            </div>
          </div>
          <button
            onClick={() => {
              setShowSuccessToast(false);
              setSuccessToastMessage('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '0.25rem',
              opacity: 0.7,
              transition: 'opacity 0.2s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '0.7'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
      </div>
    </>
  );
};

export default HeadteacherDashboard;
